import "./reviewPage.css";
import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star } from "lucide-react";
import dayjs from "dayjs";
import usersRequest from "../../request/usersRequest";
import scheduleBasedRequest from "../../request/scheduleBasedRequest"; // depends where submitReview lives
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import BannerMessage from "../common/bannerMessage/bannerMessage";

// Qualitative label per score. Index 0 = no rating yet.
const RATING_LABELS = {
	en: ["Tap to rate", "Terrible", "Poor", "Okay", "Great", "Excellent"],
	es: ["Toca para calificar", "Muy malo", "Malo", "Regular", "Bueno", "Excelente"],
};

const ReviewPage = () => {
	const { bookingId } = useParams();
	const navigate = useNavigate();
	const { language, userData } = useContext(CombinedContext);

	const [loading, setLoading] = useState(true);
	const [booking, setBooking] = useState(null);

	const [rating, setRating] = useState(0);
	const [hoverRating, setHoverRating] = useState(0);
	const [comment, setComment] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [existingReview, setExistingReview] = useState(null);
	const [isEditMode, setIsEditMode] = useState(false);

	const MAX_CHARS = 250;

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);

		setTimeout(() => {
			setExiting(true);
		}, duration - 250);

		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	useEffect(() => {
		async function load() {
			try {
				const res = await usersRequest.getBooking(bookingId);

				setBooking(res.booking);

				if (res.review) {
					setExistingReview(res.review);
					setRating(res.review.rating);
					setComment(res.review.comment || "");
					setIsEditMode(true);
				}
			} catch (err) {
				console.error("Failed to load booking:", err);
			}
			setLoading(false);
		}
		load();
	}, [bookingId]);

	async function handleSubmit() {
		if (rating === 0) {
			showBanner("error", translations[language].reviewPage?.errorNoRating || "Please select a rating");
			return;
		}

		setSubmitting(true);

		try {
			if (isEditMode) {
				await usersRequest.updateReview(existingReview.reviewId, rating, comment);
			} else {
				await usersRequest.createReview(bookingId, rating, comment);
			}

			navigate(`/booking/${bookingId}`, {
				state: { reviewSubmitted: true, isEdit: isEditMode },
			});
		} catch (err) {
			console.error("Review submit failed:", err);
			showBanner("error", translations[language].reviewPage?.errorSubmit || "Failed to submit review");
		} finally {
			setSubmitting(false);
		}
	}

	if (loading || !booking) return <div className="loading">Loading...</div>;

	const labels = RATING_LABELS[language] || RATING_LABELS.en;
	const displayRating = hoverRating || rating;
	const ratingLabel = labels[displayRating];

	const serviceNames = Array.isArray(booking.services)
		? booking.services.map((s) => (language === "es" ? s.serviceNameEs : s.serviceNameEn) || s.name).filter(Boolean)
		: [];

	const timeLabel = booking.startTime ? dayjs(booking.startTime).format("h:mm A") : null;

	return (
		<div className="sf-review-page">
			{/* HEADER */}
			<div className="sf-reviewPage-header">
				<button className="sf-review-back" onClick={() => navigate(-1)} aria-label="Go back">
					<ArrowLeft size={20} />
				</button>
				<div className="sf-reviewPage-header-title">
					{isEditMode
						? translations[language].reviewPage?.editTitle || "Edit your review"
						: translations[language].reviewPage?.title || "Leave a review"}
				</div>
			</div>

			{/* CONTEXT — what is being reviewed */}
			<div className="sf-review-context">
				<div className="sf-review-context-shop">{booking.shopName}</div>
				<div className="sf-review-context-meta">
					{dayjs(booking.date).format("MMM D, YYYY")}
					{timeLabel && <span className="sf-review-context-dot">·</span>}
					{timeLabel}
				</div>
				{serviceNames.length > 0 && (
					<div className="sf-review-context-services">
						{serviceNames.map((name, i) => (
							<span key={i} className="sf-review-service-chip">
								{name}
							</span>
						))}
					</div>
				)}
			</div>

			{/* RATING — the hero */}
			<div className="sf-review-rating-block">
				<div className="sf-stars" onMouseLeave={() => setHoverRating(0)}>
					{[1, 2, 3, 4, 5].map((num) => (
						<span
							key={num}
							className={`sf-star ${displayRating >= num ? "active" : ""}`}
							onClick={() => setRating(num)}
							onMouseEnter={() => setHoverRating(num)}
							role="button"
							aria-label={`${num} ${num === 1 ? "star" : "stars"}`}
						>
							<Star size={38} fill="currentColor" strokeWidth={0.5} />
						</span>
					))}
				</div>
				<div className={`sf-review-rating-label ${displayRating ? "set" : ""}`}>{ratingLabel}</div>
			</div>

			{/* COMMENT */}
			<div className="sf-review-comments">
				<label className="sf-review-comment-label" htmlFor="sf-review-text">
					{translations[language].reviewPage?.commentLabel || (language === "es" ? "Agrega un comentario (opcional)" : "Add a comment (optional)")}
				</label>
				<textarea
					id="sf-review-text"
					className="sf-review-textarea"
					placeholder={translations[language].reviewPage?.placeholder || "Share your experience..."}
					value={comment}
					maxLength={MAX_CHARS}
					onChange={(e) => setComment(e.target.value)}
				/>
				<div className="sf-review-charcount">
					{comment.length}/{MAX_CHARS}
				</div>
			</div>

			{isEditMode && (
				<div className="sf-review-edit-hint">
					{translations[language].reviewPage?.editHint ||
						(language === "es"
							? "Ya reseñaste esta cita. Puedes actualizar tu reseña abajo."
							: "You’ve already reviewed this booking. You can update it below.")}
				</div>
			)}

			{/* SUBMIT */}
			<button className="sf-review-submit" onClick={handleSubmit} disabled={submitting}>
				{submitting
					? translations[language].reviewPage?.submitting || "Saving..."
					: isEditMode
						? translations[language].reviewPage?.save || "Save changes"
						: translations[language].reviewPage?.submit || "Submit review"}
			</button>

			{banner && <BannerMessage type={banner.type} text={banner.text} exiting={exiting} />}
		</div>
	);
};

export default ReviewPage;